/**
 * Zones Settings Page (Phase 9.2) - UI label "Zones", database table "stations"
 *
 * Manage organization zones (equipment centers / work areas) with CRUD operations.
 * Features search, department filter, and modal form for create/edit.
 * Zones group cameras and assessments by physical location for zone-based analytics.
 */

import { useState, useEffect } from 'react';
import {
  Locate,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Camera,
  ClipboardCheck,
  Pencil,
  Trash2,
  X,
  Building2,
  ChevronDown,
  MapPin,
} from 'lucide-react';
import { useStations, useDeleteStation, useCreateStation, useUpdateStation } from '../../hooks/useStations';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useCurrentUser } from '../../hooks/useUsers';
import type { StationWithCount, CreateStation } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function StationsPage() {
  useDocumentTitle('Zones');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<StationWithCount | null>(null);

  const { data: stations, isLoading, error } = useStations({
    search: searchQuery || undefined,
    departmentId: departmentFilter || undefined,
    includeInactive: showInactive,
  });

  const { data: departments } = useDepartmentLookup();
  const deleteMutation = useDeleteStation();

  const handleCreate = () => {
    setEditingStation(null);
    setIsFormOpen(true);
  };

  const handleEdit = (station: StationWithCount) => {
    setEditingStation(station);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this zone?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingStation(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50">
            <Locate size={24} className="text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Zones
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage equipment centers and work areas for zone-based analytics
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add Zone
          </button>
        )}
      </div>

      {/* Filters Bar — only show when there is data or an active search/filter */}
      {((stations && stations.length > 0) || searchQuery || departmentFilter) && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[280px]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search zones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all duration-200
              "
            />
          </div>

          {/* Department filter */}
          <div className="relative">
            <Building2
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="
                appearance-none rounded-xl border border-gray-200 bg-white
                py-2.5 pl-9 pr-10
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="">All Departments</option>
              {departments?.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>

          {/* Inactive filter */}
          {isAdmin && (
            <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-600">
                Show inactive
              </span>
            </label>
          )}
        </div>
      )}

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load zones
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading zones...</p>
        </div>
      ) : stations && stations.length > 0 ? (
        <StationList
          stations={stations}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <StationForm
          station={editingStation}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// STATION LIST COMPONENT
// ============================================

interface StationListProps {
  stations: StationWithCount[];
  isAdmin: boolean;
  onEdit: (station: StationWithCount) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function StationList({ stations, isAdmin, onEdit, onDelete, isDeleting }: StationListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Zone
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Code
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Location
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Department
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Cameras
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Assessments
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {stations.map((station) => (
            <tr
              key={station.id}
              className={`
                group transition-colors
                ${station.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-lg
                      ${station.isActive ? 'bg-amber-50' : 'bg-gray-100'}
                    `}
                  >
                    <Locate
                      size={16}
                      className={station.isActive ? 'text-amber-600' : 'text-gray-400'}
                    />
                  </div>
                  <span
                    className={`
                      text-sm font-semibold
                      ${station.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {station.name}
                  </span>
                </div>
              </td>

              {/* Code */}
              <td className="px-6 py-4 whitespace-nowrap">
                <code
                  className={`
                    text-xs font-mono px-2 py-1 rounded
                    ${station.isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}
                  `}
                >
                  {station.code}
                </code>
              </td>

              {/* Location */}
              <td className="px-6 py-4 whitespace-nowrap">
                {station.location ? (
                  <div className="flex items-center gap-1.5">
                    <MapPin
                      size={14}
                      className={station.isActive ? 'text-gray-400' : 'text-gray-300'}
                    />
                    <span
                      className={`
                        text-sm
                        ${station.isActive ? 'text-gray-600' : 'text-gray-400'}
                      `}
                    >
                      {station.location}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-300 italic text-sm">None</span>
                )}
              </td>

              {/* Department */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    text-sm
                    ${station.isActive ? 'text-gray-600' : 'text-gray-400'}
                  `}
                >
                  {station.departmentName || (
                    <span className="text-gray-300 italic">None</span>
                  )}
                </span>
              </td>

              {/* Camera Count */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Camera
                    size={16}
                    className={station.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm font-medium
                      ${station.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {station.cameraCount}
                  </span>
                </div>
              </td>

              {/* Assessment Count */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <ClipboardCheck
                    size={16}
                    className={station.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm font-medium
                      ${station.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {station.assessmentCount}
                  </span>
                </div>
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${station.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${station.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {station.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(station)}
                      className="
                        inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                        text-sm font-medium text-gray-600
                        hover:bg-gray-100 hover:text-gray-900
                        transition-colors
                      "
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    {station.isActive && (
                      <button
                        onClick={() => onDelete(station.id)}
                        disabled={isDeleting}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-red-600
                          hover:bg-red-50 hover:text-red-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// STATION FORM COMPONENT
// ============================================

interface StationFormProps {
  station: StationWithCount | null;
  onClose: () => void;
}

function StationForm({ station, onClose }: StationFormProps) {
  const isEdit = !!station;
  const [name, setName] = useState(station?.name ?? '');
  const [code, setCode] = useState(station?.code ?? '');
  const [departmentId, setDepartmentId] = useState(station?.departmentId ?? '');
  const [description, setDescription] = useState(station?.description ?? '');
  const [location, setLocation] = useState(station?.location ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: departments } = useDepartmentLookup();
  const createMutation = useCreateStation();
  const updateMutation = useUpdateStation();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when station changes
  useEffect(() => {
    setName(station?.name ?? '');
    setCode(station?.code ?? '');
    setDepartmentId(station?.departmentId ?? '');
    setDescription(station?.description ?? '');
    setLocation(station?.location ?? '');
    setError(null);
  }, [station]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!code.trim()) {
      setError('Code is required');
      return;
    }

    if (!/^[a-z0-9_-]+$/i.test(code)) {
      setError('Code must be alphanumeric with underscores or hyphens');
      return;
    }

    try {
      if (isEdit && station) {
        await updateMutation.mutateAsync({
          id: station.id,
          data: {
            name: name.trim(),
            code: code.trim().toLowerCase(),
            departmentId: departmentId || undefined,
            description: description.trim() || undefined,
            location: location.trim() || undefined,
          },
        });
      } else {
        const data: CreateStation = {
          name: name.trim(),
          code: code.trim().toLowerCase(),
          departmentId: departmentId || undefined,
          description: description.trim() || undefined,
          location: location.trim() || undefined,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50">
              <Locate size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Zone' : 'New Zone'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update zone details' : 'Create a new equipment center or work area'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Line 37, Dock A, CIP Zone"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Code Field */}
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Code <span className="text-red-500">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="e.g., line-37, dock-a"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm font-mono
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
              />
              <p className="mt-1.5 text-xs text-gray-400">
                Short identifier for reports (letters, numbers, underscores, hyphens)
              </p>
            </div>

            {/* Location Field */}
            <div>
              <label
                htmlFor="location"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Location
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <div className="relative">
                <MapPin
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Building A, Floor 2"
                  className="
                    w-full rounded-xl border border-gray-200 bg-white
                    pl-11 pr-4 py-3 text-sm
                    placeholder:text-gray-400
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                  disabled={isPending}
                />
              </div>
            </div>

            {/* Department Field */}
            <div>
              <label
                htmlFor="departmentId"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Department
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <div className="relative">
                <select
                  id="departmentId"
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  disabled={isPending}
                  className="
                    w-full appearance-none rounded-xl border border-gray-200 bg-white
                    px-4 py-3 pr-10 text-sm
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  <option value="">No department</option>
                  {departments?.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this zone..."
                rows={3}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Zone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <Locate size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No zones found' : 'No zones yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No zones match "${searchQuery}". Try a different search term.`
            : 'Get started by creating your first zone to organize cameras and assessments by location.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Create First Zone
          </button>
        )}
      </div>
    </div>
  );
}
